import clsx from 'clsx';
import { Calendar } from 'lucide-react';
import { Nil } from '../../../components/ui/Table';
import { dueLabel, dueToneClasses } from '../taskUtils';

interface DueDateFieldProps {
  value: string | null;
  onChange: (next: string | null) => void;
  disabled?: boolean;
  /**
   * On a card: with no date, nothing shows until the card is hovered or
   * focused ("Add date"), instead of a stray icon and dot on every card.
   */
  quietWhenEmpty?: boolean;
}

/**
 * A due date shown as a short relative chip that opens a native date picker.
 *
 * The native input is deliberate: it is a date-only control, which matches the
 * date-only column, and it brings its own locale-correct keyboard and calendar
 * on every platform without a picker dependency.
 */
export function DueDateField({ value, onChange, disabled, quietWhenEmpty }: DueDateFieldProps) {
  const label = dueLabel(value);

  return (
    <label
      className={clsx(
        'relative inline-flex cursor-pointer items-center gap-1 text-body-xs',
        value ? dueToneClasses(value) : 'text-text-muted',
        disabled && 'cursor-default opacity-60',
        quietWhenEmpty && !value && 'opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100',
      )}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <Calendar className="h-3 w-3 shrink-0" aria-hidden />
      {/* No date is the common case: a faint dot, not the words "No date" on every card */}
      {label ? <span>{label}</span> : quietWhenEmpty ? <span>Add date</span> : <Nil />}
      <input
        type="date"
        disabled={disabled}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        // Covers the chip so the whole thing is the hit target, while the styled
        // label stays visible.
        className="absolute inset-0 cursor-pointer opacity-0"
      />
    </label>
  );
}
